import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";

import SummaryCards from "./SummaryCards";
import UserTable from "./UserTable";
import AuditLogDrawer from "./AuditLogDrawer";
import EditUserDialog from "./EditUserDialog";
import AddUserDialog from "./AddUserDialog";
import RevokeDialog from "./RevokeDialog";
import DepartmentsTab from "./DepartmentsTab";

import useAccessUsers from "./useAccessUsers";
import { getSummary } from "./accessManagerApi";
import { ALL_SCOPES } from "./scopeHierarchy";
import { isAccessManagerEnabled } from "./featureFlags";
import { normalizeApiError, formatErrorMessage } from "../../utils/apiErrors";
import "./AccessManagerPanel.css";

/** Message shown when the backend blocks removal of the final admin scope (Req 8.1). */
export const LAST_ADMIN_MESSAGE =
  "The last admin scope cannot be removed — at least one administrator must remain.";

/** Message shown when the backend blocks an admin from revoking their own access (Req 9.1). */
export const SELF_REVOKE_MESSAGE =
  "Self-revocation of admin access is not permitted.";

/** Informational notice appended to success toasts (Req 12.1). */
export const CACHE_TTL_NOTICE =
  "Changes take effect within 60 seconds due to cache TTL.";

/**
 * Collects every string-ish field on an error that could carry a backend
 * error code or message, so protection errors can be recognized regardless
 * of which shape the API used.
 *
 * @param {unknown} err
 * @returns {string} lower-cased haystack
 */
function errorHaystack(err) {
  const data = err?.response?.data;
  const parts = [
    err?.message,
    err?.code,
    typeof data === "string" ? data : null,
    data?.message,
    data?.error,
    data?.code,
    data?.detail,
    data?.detail?.message,
    data?.detail?.code,
    data?.error?.message,
    data?.error?.code,
  ];
  return parts
    .filter((part) => typeof part === "string")
    .join(" ")
    .toLowerCase();
}

/**
 * Detects the two protection errors that need bespoke copy (Req 8.1, 9.1).
 *
 * @param {unknown} err
 * @returns {string|null} the specific message, or null when not a protection error
 */
export function detectProtectionError(err) {
  const haystack = errorHaystack(err);
  if (!haystack) return null;

  if (
    haystack.includes("last_admin") ||
    haystack.includes("last admin") ||
    haystack.includes("only admin") ||
    haystack.includes("at least one admin")
  ) {
    return LAST_ADMIN_MESSAGE;
  }

  if (
    haystack.includes("self_revoke") ||
    haystack.includes("self-revoke") ||
    haystack.includes("self revoke") ||
    haystack.includes("own admin") ||
    haystack.includes("your own access")
  ) {
    return SELF_REVOKE_MESSAGE;
  }

  return null;
}

/**
 * Resolves the message to display for a failed scope mutation: the specific
 * protection copy when applicable, otherwise the sanitized generic message.
 *
 * @param {unknown} err
 * @returns {string}
 */
export function resolveMutationMessage(err) {
  return detectProtectionError(err) ?? formatErrorMessage(normalizeApiError(err));
}

/**
 * Access Manager panel — root orchestrator.
 *
 * Composes the summary cards, search/filter toolbar, user table, audit drawer,
 * and the add/edit/revoke dialogs. Owns dialog state, optimistic updates with
 * rollback, and the cache-TTL success notice.
 */
export default function AccessManagerPanel() {
  const [activeTab, setActiveTab] = useState("users");
  const [searchQuery, setSearchQuery] = useState("");
  const [scopeFilter, setScopeFilter] = useState("");

  const [activeDialog, setActiveDialog] = useState(null); // "edit" | "add" | "revoke" | null
  const [selectedUser, setSelectedUser] = useState(null);
  const [auditOpen, setAuditOpen] = useState(false);

  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryError, setSummaryError] = useState(null);

  const [actionError, setActionError] = useState(null);
  // Email of the user whose scope change is currently in flight (Req 10.3).
  const [pendingEmail, setPendingEmail] = useState(null);

  const { users, loading, error, retry, optimisticUpdate, rollback } =
    useAccessUsers({ searchQuery, scopeFilter });

  const mutating = pendingEmail !== null;

  // -- Summary (Req 1.1, 1.2, 1.3) -----------------------------------------
  const loadSummary = useCallback(async () => {
    setSummaryLoading(true);
    setSummaryError(null);
    try {
      const response = await getSummary();
      const data = response?.data;
      setSummary(data?.summary ?? data ?? null);
    } catch (err) {
      setSummaryError(formatErrorMessage(normalizeApiError(err)));
    } finally {
      setSummaryLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  // -- Dialog plumbing -----------------------------------------------------
  const closeDialog = useCallback(() => {
    setActiveDialog(null);
    setSelectedUser(null);
  }, []);

  const handleEdit = useCallback((user) => {
    setSelectedUser(user);
    setActiveDialog("edit");
  }, []);

  const handleRevoke = useCallback((user) => {
    setSelectedUser(user);
    setActiveDialog("revoke");
  }, []);

  const handleAddClick = useCallback(() => {
    setSelectedUser(null);
    setActiveDialog("add");
  }, []);

  /**
   * Applies a scope change optimistically, rolling back and surfacing a
   * message when the change fails (Req 8.1, 8.2, 9.1, 9.2, 10.1, 10.2, 10.3).
   *
   * The error is re-thrown so the originating dialog stays open for
   * correction (Req 4.5, 5.5, 6.5).
   */
  const applyScopeChange = useCallback(
    async ({ email, previousScopes, nextScopes, mutate }) => {
      setActionError(null);
      setPendingEmail(email);
      optimisticUpdate(email, nextScopes);

      try {
        if (mutate) {
          await mutate();
        }
        toast.success(`Access updated for ${email}. ${CACHE_TTL_NOTICE}`);
        loadSummary();
      } catch (err) {
        rollback(email, previousScopes);
        const message = resolveMutationMessage(err);
        setActionError(message);
        toast.error(message);
        throw err;
      } finally {
        setPendingEmail(null);
      }
    },
    [optimisticUpdate, rollback, loadSummary]
  );

  // Edit dialog already performed the PUT; reflect it immediately (Req 4.6).
  const handleEditSave = useCallback(
    (email, scopes) =>
      applyScopeChange({
        email,
        previousScopes: selectedUser?.scopes ?? [],
        nextScopes: scopes,
      }),
    [applyScopeChange, selectedUser]
  );

  // Revoke dialog already revoked each scope; drop them all locally (Req 6.6).
  const handleRevokeConfirm = useCallback(
    (email) =>
      applyScopeChange({
        email,
        previousScopes: selectedUser?.scopes ?? [],
        nextScopes: [],
      }),
    [applyScopeChange, selectedUser]
  );

  // New users are not in the local list yet, so refetch (Req 5.6).
  const handleAddUser = useCallback(
    async (email) => {
      setActionError(null);
      await retry();
      toast.success(`Access granted to ${email}. ${CACHE_TTL_NOTICE}`);
      loadSummary();
    },
    [retry, loadSummary]
  );

  // -- Feature flag gating (Req 11.1) --------------------------------------
  if (!isAccessManagerEnabled()) {
    return null;
  }

  return (
    <div className="access-manager-panel">
      <div className="access-manager-panel__header">
        <h2 className="access-manager-panel__title">Access Manager</h2>
        <div className="access-manager-panel__header-actions">
          <button
            type="button"
            className="access-manager-panel__audit-btn"
            onClick={() => setAuditOpen(true)}
          >
            Audit Log
          </button>
          <button
            type="button"
            className="access-manager-panel__add-btn"
            onClick={handleAddClick}
            disabled={mutating}
          >
            Add User
          </button>
        </div>
      </div>

      {/* Tab control (Req 2.1) */}
      <div className="access-manager-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          className={`access-manager-tabs__tab${activeTab === "users" ? " access-manager-tabs__tab--active" : ""}`}
          aria-selected={activeTab === "users"}
          aria-controls="tabpanel-users"
          onClick={() => setActiveTab("users")}
        >
          Users
        </button>
        <button
          type="button"
          role="tab"
          className={`access-manager-tabs__tab${activeTab === "departments" ? " access-manager-tabs__tab--active" : ""}`}
          aria-selected={activeTab === "departments"}
          aria-controls="tabpanel-departments"
          onClick={() => setActiveTab("departments")}
        >
          Departments
        </button>
      </div>

      {/* Users tab panel */}
      {activeTab === "users" && (
        <div role="tabpanel" id="tabpanel-users" aria-label="Users">
          <SummaryCards
            summary={summary}
            loading={summaryLoading}
            error={summaryError}
            onRetry={loadSummary}
          />

          <div className="access-manager-panel__toolbar">
            <div className="access-manager-panel__toolbar-field">
              <label
                htmlFor="access-manager-search"
                className="access-manager-panel__toolbar-label"
              >
                Search users
              </label>
              <input
                id="access-manager-search"
                type="search"
                className="access-manager-panel__search-input"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Filter by email"
                autoComplete="off"
              />
            </div>

            <div className="access-manager-panel__toolbar-field">
              <label
                htmlFor="access-manager-scope-filter"
                className="access-manager-panel__toolbar-label"
              >
                Scope
              </label>
              <select
                id="access-manager-scope-filter"
                className="access-manager-panel__scope-select"
                value={scopeFilter}
                onChange={(e) => setScopeFilter(e.target.value)}
                disabled={mutating}
              >
                <option value="">All scopes</option>
                {ALL_SCOPES.map((scope) => (
                  <option key={scope} value={scope}>
                    {scope}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {actionError && (
            <div className="access-manager-panel__error" role="alert">
              {actionError}
            </div>
          )}

          <UserTable
            users={users}
            loading={loading}
            error={error}
            onRetry={retry}
            onEdit={handleEdit}
            onRevoke={handleRevoke}
            searchQuery={searchQuery}
            scopeFilter={scopeFilter}
          />
        </div>
      )}

      {/* Departments tab panel */}
      {activeTab === "departments" && (
        <div role="tabpanel" id="tabpanel-departments" aria-label="Departments">
          <DepartmentsTab />
        </div>
      )}

      {/* Dialogs and drawer stay at panel level, outside tab panels */}
      <EditUserDialog
        open={activeDialog === "edit"}
        user={selectedUser}
        onClose={closeDialog}
        onSave={handleEditSave}
      />

      <AddUserDialog
        open={activeDialog === "add"}
        onClose={closeDialog}
        onAdd={handleAddUser}
      />

      <RevokeDialog
        open={activeDialog === "revoke"}
        user={selectedUser}
        onClose={closeDialog}
        onConfirm={handleRevokeConfirm}
      />

      <AuditLogDrawer open={auditOpen} onClose={() => setAuditOpen(false)} />
    </div>
  );
}
